import React, { useEffect, useState } from 'react';
import { Button, Spinner, OverlayTrigger, Tooltip, Row } from 'react-bootstrap';
import MonitoringProjectsTable from './MonitoringProjectsTable';
import { PickList } from 'primereact/picklist';
import { DownloadFile } from 'icone/index';
import {
  postMonitoringProjects,
  getMonitorableProjects,
  getMonitoringProjects,
  getMonitoringSelection,
} from '@lib/api';
import { DangerToast, SuccessToast } from 'components/GlobalToastList';
import { exportCreatedMonitoringExcel } from '@lib/api/index';

// const messages = [
//   'Individuando i progetti da monitorare',
//   'Predisponendo i progetti',
//   'Preparando la lista',
// ];

// randomMode: la lista dei selezionati viene proposta dal BE con il metodo dei requisiti
// (fascia centrale della distribuzione ESG + code al 5%). Resta comunque modificabile
// — si possono aggiungere progetti cercandoli per NDG, rimuoverne, o ri-estrarre.
const MonitoringProjectsCTA = ({ randomMode = false }) => {
  const [source, setSource] = useState([]);
  const [target, setTarget] = useState([]);
  const [monitorableProjects, setMonitorableProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isMonitoringLoading, setIsMonitoringLoading] = useState(false);
  const [monitoredProjects, setMonitoredProjects] = useState([]);
  const [monitoringGroupId, setMonitoringGroupId] = useState(null);
  const [sampleSize, setSampleSize] = useState(30);
  const [selectionStats, setSelectionStats] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);

      try {
        // const retrievedProjects = await getProjects();
        const retrievedProjects = await getMonitorableProjects();
        const filteredProjects = retrievedProjects
          // .filter((project) => project.status === 'SUBMITTED' && !!!project?.isMonitoring)
          .map((project) => ({
            clientId: project.clientId,
            requestId: project.requestId,
            rating:
              project.esgratings
                ?.filter((el) => el.type === 'total')
                ?.map((total) => total.rating)
                ?.join(', ') || 'N/A',
            projectId: project.id,
          }));

        setMonitorableProjects(filteredProjects);
      } catch (error) {
        console.error('Errore nel recupero dei progetti:', error);
        // opzionale: setError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  useEffect(() => {
    if (monitorableProjects.length > 0) setSource(monitorableProjects);
  }, [monitorableProjects]);

  const itemTemplate = (project) => {
    return (
      <div className='flex flex-wrap p-2 align-items-center gap-3'>
        <div className='flex-1 flex flex-column gap-2'>
          <span>NDG: </span>
          <span className='font-weight-bold'>{project.clientId}</span>
          <div className='flex align-items-center gap-2'>
            <span>N.Rapporto: </span>
            <span className='font-weight-bold'>{project.requestId}</span>
          </div>
        </div>
        <span>Rating ESG: </span>
        <span className='font-weight-bold'>{project.rating}</span>
      </div>
    );
  };

  // const startFakeApiCall = () => {
  //   if (monitoredProjects.length > 0) return;

  //   setLoading(true);
  //   setMessageIndex(0);
  //   setShowMessage(true);

  //   let step = 0;
  //   const interval = setInterval(() => {
  //     setShowMessage(false);
  //     setTimeout(() => {
  //       setMessageIndex((prev) => prev + 1);
  //       setShowMessage(true);
  //     }, 300);
  //     step++;
  //     if (step >= messages.length - 1) clearInterval(interval);
  //   }, 1600);
  //   if (!monitorableProjects || monitorableProjects.length === 0) {
  //     setLoading(false);
  //     DangerToast('Attenzione', 'Errore nel ricercare potenziali progetti da monitorare');
  //     return;
  //   }
  //   setTimeout(() => {
  //     setLoading(false);
  //     setMessageIndex(0);

  //     const { picked, rest } = pickRandom(monitorableProjects, 10);

  //     setTarget(picked);
  //     setSource(rest);
  //   }, 5000);
  // Abilitare una volta pronto il BE
  const submitMonitoring = async () => {
    if (target.length === 0) return;

    setLoading(true);
    const selectedProjectsArray = target.map((item) => item.projectId);

    try {
      const resultOfMonitoringCall = await postMonitoringProjects({ ids: selectedProjectsArray });
      SuccessToast('Operazione conclusa', 'Monitoraggio avviato correttamente');

      getProjectsForTable(resultOfMonitoringCall.monitoringGroup);
    } catch (error) {
      DangerToast('Attenzione', 'Errore nella creazione del monitoraggio');
    } finally {
      setLoading(false);
    }
  };

  const getProjectsForTable = async (monitoringId) => {
    setIsMonitoringLoading(true);
    try {
      const projects = await getMonitoringProjects(monitoringId);
      setMonitoredProjects(
        projects.map((project) => ({
          clientId: project.clientId,
          requestId: project.requestId,
          rating:
            project.esgratings
              ?.filter((el) => el.type === 'total')
              ?.map((total) => total.rating)
              ?.join(', ') || 'N/A',
          projectId: project.id,
        })),
      );
      setMonitoringGroupId(monitoringId);
    } catch (error) {
      DangerToast('Attenzione', 'Errore nel recupero dei progetti monitorati');
    } finally {
      setIsMonitoringLoading(false);
    }
  };

  const onChange = (event) => {
    setSource(event.source);
    setTarget(event.target);
  };

  // Estrazione casuale: sovrascrive la selezione corrente e rimette fra i disponibili
  // i progetti precedentemente proposti, così l'operazione è ripetibile.
  const drawRandomSample = async () => {
    setIsDrawing(true);
    try {
      const { projects: proposed, stats } = await getMonitoringSelection({ size: sampleSize });
      if (!proposed || proposed.length === 0) {
        DangerToast('Attenzione', 'Nessun progetto eleggibile per l’estrazione');
        return;
      }
      const proposedIds = new Set(proposed.map((p) => p.projectId));
      const pool = [...source, ...target];
      setTarget(pool.filter((p) => proposedIds.has(p.projectId)));
      setSource(pool.filter((p) => !proposedIds.has(p.projectId)));
      setSelectionStats(stats);
      SuccessToast('Estrazione completata', `${proposed.length} progetti selezionati`);
    } catch (error) {
      DangerToast('Attenzione', 'Errore durante l’estrazione casuale');
    } finally {
      setIsDrawing(false);
    }
  };
  const downloadTooltip = (props) => <Tooltip {...props}>Download Excel</Tooltip>;
  return (
    <div className='text-center'>
      <p>
        {randomMode
          ? 'Questa sezione estrae casualmente un campione di progetti da monitorare. La selezione proposta è modificabile prima della conferma.'
          : 'Questa sezione permette di avviare un monitoraggio da selezione dei progetti.'}
      </p>

      {randomMode && (
        <div className='d-flex justify-content-center align-items-center gap-2 my-3'>
          <label htmlFor='sampleSize' className='m-0'>
            Numerosità campione
          </label>
          <input
            id='sampleSize'
            type='number'
            min='1'
            max='200'
            value={sampleSize}
            onChange={(e) => setSampleSize(Number(e.target.value))}
            style={{ width: '5rem' }}
            className='form-control mx-2'
          />
          <Button className='btn btn-primary' onClick={drawRandomSample} disabled={isDrawing}>
            {isDrawing ? 'Estrazione…' : target.length > 0 ? 'Nuova estrazione' : 'Estrai campione'}
          </Button>
        </div>
      )}

      {randomMode && selectionStats && (
        <p className='small text-muted'>
          Estratti {selectionStats.selected} progetti su {selectionStats.candidates} eleggibili:{' '}
          {selectionStats.central} dalla fascia centrale e {selectionStats.tails} dalle code (ESG
          medio {selectionStats.meanEsg}, deviazione standard {selectionStats.stdDevEsg}).
        </p>
      )}

      {/* {loading ? (
        <div className='mt-4'>
          <div
            style={{
              opacity: showMessage ? 1 : 0,
              transition: 'opacity 0.3s ease-in-out',
              minHeight: '2rem',
              fontWeight: 'bold',
            }}>
            {messages[messageIndex]}
          </div>
          <Spinner animation='border' role='status' className='my-3'>
            <span style={{ visibility: 'hidden' }}>Loading...</span>
          </Spinner>
        </div>
      ) : (
        <div className='d-flex justify-content-center my-4'>
          <Button
            className='btn btn-primary'
            onClick={startFakeApiCall}
            disabled={monitoredProjects.length > 0 || isMonitoringLoading}>
            Avvia individuazione progetti
          </Button>
        </div>
      )} */}
      {loading && (
        <div className='mt-4'>
          <Spinner animation='border' role='status' className='my-3'>
            <span style={{ visibility: 'hidden' }}>Loading...</span>
          </Spinner>
        </div>
      )}
      {source.length === 0 && (
        <div className='my-4'>
          <p className='font-weight-bold'>Nessun progetto da poter monitorare in questo momento.</p>
        </div>
      )}
      {(source.length > 0 || target.length > 0) && (
        <div className='my-4'>
          <PickList
            source={source}
            target={target}
            onChange={onChange}
            itemTemplate={itemTemplate}
            filter
            filterBy='clientId,requestId,rating'
            breakpoint='1400px'
            sourceHeader='Disponibili'
            targetHeader='Selezionati'
            sourceStyle={{ height: '30rem' }}
            targetStyle={{ height: '30rem' }}
          />

          <div className='d-flex justify-content-center mt-4'>
            <Button
              className='btn btn-primary'
              onClick={submitMonitoring}
              disabled={monitoredProjects.length > 0 || isMonitoringLoading || target.length === 0}>
              Avvia Monitoraggio
            </Button>
          </div>
        </div>
      )}
      {isMonitoringLoading ? (
        <div className='d-flex justify-content-center align-items-center gap-1'>
          <Spinner animation='border' role='status' />
          <span>Caricamento...</span>
        </div>
      ) : null}
      {monitoredProjects.length > 0 && !isMonitoringLoading && (
        <div>
          <div className='d-flex justify-content-end'>
            <OverlayTrigger placement='top' overlay={downloadTooltip}>
              <Button
                variant='info'
                onClick={() => exportCreatedMonitoringExcel(monitoringGroupId)}
                aria-label='Download Excel'
                className='ml-2 lineheight-initial'
                disabled={isMonitoringLoading}>
                <Row
                  className='align-items-center justify-content-center'
                  style={{ padding: '0.25rem 0.7rem' }}>
                  {isMonitoringLoading ? (
                    'Loading…'
                  ) : (
                    <DownloadFile width='23px' className='light-color m-0' />
                  )}
                </Row>
              </Button>
            </OverlayTrigger>
          </div>
          <MonitoringProjectsTable rowData={monitoredProjects} />
        </div>
      )}
    </div>
  );
};

export default MonitoringProjectsCTA;
